import { Avatar, AvatarFallback } from '@ooh/ui/components/avatar';
import { Badge } from '@ooh/ui/components/badge';
import { BRAND_MARK } from '../../lib/brand';
import type { ClientOfferReviewStatusPillModel } from './client-offer-review-banner-model';
import type {
  ClientOfferReviewAccessMode,
  ClientOfferReviewHeaderLabels,
} from './client-offer-review-types';

// DS-BYPASS: this SSO bar is an "always near-black" chrome surface, independent of
// the app's light/dark theme (packages/ui/src/styles/globals.css has no such
// always-dark surface token — every existing token flips with `.dark`). Same
// reasoning applies to the `text-zinc-400` subtitle below and to the lightbox
// modal's always-dark overlay chrome (client-offer-review-lightbox-modal.tsx).
const SSO_BAR_CLASS = 'bg-zinc-900';

/** Optional SSO bar + main header (brand avatar, title, version + status pills). */
export function ClientOfferReviewHeader({
  accessMode,
  offerTitle,
  offerMeta,
  loggedInEmail,
  versionNumber,
  statusPill,
  labels,
}: {
  accessMode: ClientOfferReviewAccessMode;
  offerTitle: string;
  /** Pre-composed subtitle line, e.g. "{clientName} · opublikowano {date}". */
  offerMeta: string;
  loggedInEmail: string;
  versionNumber: number;
  statusPill: ClientOfferReviewStatusPillModel;
  labels: ClientOfferReviewHeaderLabels;
}) {
  return (
    <>
      {accessMode === 'Zalogowany (SSO)' ? (
        <div
          className={`flex items-center justify-between px-6 py-2 text-xs text-white ${SSO_BAR_CLASS}`}
        >
          <span className="inline-flex items-center gap-2 font-semibold">
            <Avatar
              data-testid="client-offer-review-sso-mark"
              className="h-5 w-auto min-w-5 rounded-sm"
            >
              <AvatarFallback className="rounded-sm bg-primary px-1 text-2xs font-bold">
                {BRAND_MARK}
              </AvatarFallback>
            </Avatar>
            {labels.brandName}
          </span>
          <span className="text-zinc-400">{`${labels.loggedInAs} ${loggedInEmail}`}</span>
        </div>
      ) : null}
      <div
        data-co-hdr
        className="flex shrink-0 flex-wrap items-center gap-4 border-b border-border px-6 py-3.5"
      >
        <Avatar data-testid="client-offer-review-avatar" className="h-11 w-11 shrink-0 rounded-xl">
          <AvatarFallback className="rounded-xl bg-primary text-base font-bold text-primary-foreground">
            {BRAND_MARK}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col">
          <span className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold leading-tight">
            {offerTitle}
          </span>
          <span className="text-xs text-muted-foreground">{offerMeta}</span>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <span className="inline-flex items-center rounded-full border border-border bg-muted px-3 py-1 text-xs font-semibold">
            {`${labels.versionPrefix} ${versionNumber}`}
          </span>
          <Badge variant={statusPill.variant} className="px-3 py-1">
            {statusPill.label}
          </Badge>
        </div>
      </div>
    </>
  );
}
